import styles from './CompanyInformation.module.css';
import loginStyles from '../../Login/Login.module.css';
import ClickAwayListener from 'react-click-away-listener';
import { useFormik } from 'formik';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  addCompanyInformation,
  getKYCBusinessTypes,
  getTenantCompany,
  getTenantDetails,
} from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { AuthBrandedLoader } from '../../Login/AuthBrandedLoader';
import { getCompanyInformationSchema } from '../../../helpers/utils/kybSchema';
import { trackOnboardingStepCompleted } from '../../../helpers/utils/analytics';

const initialValues = {
  name: '',
  displayName: '',
  kycBusinessTypeId: '',
  otherBusinessType: '',
};

const CompanyInformation = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isBusinessTypeOpen, setIsBusinessTypeOpen] = useState(false);
  const [kycBusinessTypes, setKycBusinessTypes] = useState<
    { kycBusinessTypeId: string; value: string }[]
  >([]);
  const [keyboardSelectedIndex, setKeyboardSelectedIndex] = useState<number>(-1);
  const optionsRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const typeAheadRef = useRef<string>('');
  const typeAheadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const validationSchema = useMemo(() => getCompanyInformationSchema(), []);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      try {
        const requestBody = {
          name: values.name?.trim(),
          displayName: values.displayName?.trim() || undefined,
          kycBusinessTypeId: values.kycBusinessTypeId,
        };

        if (formik.isValid) {
          await addCompanyInformation(auth?.data?.token, requestBody);
          setStatus('');
          setLoading(false);
          trackOnboardingStepCompleted('company_information');
          navigate('/kyb/verify');
        } else {
          setLoading(false);
        }
      } catch (error) {
        setStatus('Please fill all required fields');
        console.error('Error Adding Company Information:', error);
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  const selectedTypeLabel = useMemo(() => {
    return kycBusinessTypes.find(
      (type) => type.kycBusinessTypeId?.toString() === formik.values.kycBusinessTypeId?.toString()
    )?.value;
  }, [kycBusinessTypes, formik.values.kycBusinessTypeId]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch both KYC Business Types and existing company info
        const [kycTypesResponse, companyInfoResponse] = await Promise.all([
          getKYCBusinessTypes(auth?.data?.token),
          getTenantCompany(auth?.data?.token),
        ]);

        // Set business types
        setKycBusinessTypes(kycTypesResponse);

        // If company info exists, populate form
        if (companyInfoResponse?.data) {
          const data = companyInfoResponse.data;
          const selectedTypeId = data.kycBusinessTypeId?.toString() || '';

          formik.setValues({
            name: data.name || '',
            displayName: data.displayName || '',
            kycBusinessTypeId: selectedTypeId,
            otherBusinessType: (data as any).otherBusinessType || '',
          });
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [auth?.data?.token]);

  useEffect(() => {
    if (!isBusinessTypeOpen) {
      setKeyboardSelectedIndex(-1);
    }
  }, [isBusinessTypeOpen]);

  const handleBackClick = async () => {
    try {
      const tenantDetails = await getTenantDetails(auth?.data?.token);
      if (tenantDetails?.data?.tenantBuildings.length === 0) {
        navigate('/tenant-type');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching tenant details:', error);
      // Fallback to dashboard if there's an error
      navigate('/dashboard');
    }
  };

  const handleBusinessTypeKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        setIsBusinessTypeOpen(!isBusinessTypeOpen);
        setKeyboardSelectedIndex(-1);
        break;
      case 'ArrowDown': {
        e.preventDefault();
        if (!isBusinessTypeOpen) {
          setIsBusinessTypeOpen(true);
          setKeyboardSelectedIndex(0);
          setTimeout(() => optionsRefs.current[0]?.focus(), 0);
        } else {
          const nextIndex = Math.min(keyboardSelectedIndex + 1, kycBusinessTypes.length - 1);
          setKeyboardSelectedIndex(nextIndex);
          setTimeout(() => optionsRefs.current[nextIndex]?.focus(), 0);
        }
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        if (isBusinessTypeOpen) {
          const prevIndex = Math.max(keyboardSelectedIndex - 1, 0);
          setKeyboardSelectedIndex(prevIndex);
          setTimeout(() => optionsRefs.current[prevIndex]?.focus(), 0);
        }
        break;
      }
      case 'Escape':
        e.preventDefault();
        setIsBusinessTypeOpen(false);
        setKeyboardSelectedIndex(-1);
        break;
      default: {
        // Type-ahead: open dropdown and jump to matching option
        if (e.key.length === 1 && /^[a-z0-9]$/i.test(e.key)) {
          e.preventDefault();
          if (!isBusinessTypeOpen) {
            setIsBusinessTypeOpen(true);
          }
          if (typeAheadTimeoutRef.current) {
            clearTimeout(typeAheadTimeoutRef.current);
          }
          typeAheadRef.current += e.key.toLowerCase();
          const matchedIndex = kycBusinessTypes.findIndex((item) =>
            item.value.toLowerCase().startsWith(typeAheadRef.current)
          );
          if (matchedIndex !== -1) {
            setKeyboardSelectedIndex(matchedIndex);
            setTimeout(() => optionsRefs.current[matchedIndex]?.focus(), 0);
          }
          typeAheadTimeoutRef.current = setTimeout(() => {
            typeAheadRef.current = '';
          }, 500);
        }
        break;
      }
    }
  };

  const handleOptionKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLButtonElement>,
    type: { kycBusinessTypeId: string; value: string }
  ) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        formik.setFieldValue('kycBusinessTypeId', type.kycBusinessTypeId.toString());
        formik.setFieldTouched('kycBusinessTypeId', true);
        setIsBusinessTypeOpen(false);
        setKeyboardSelectedIndex(-1);
        break;
      case 'ArrowDown': {
        e.preventDefault();
        const nextIndex = Math.min(index + 1, kycBusinessTypes.length - 1);
        setKeyboardSelectedIndex(nextIndex);
        setTimeout(() => optionsRefs.current[nextIndex]?.focus(), 0);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prevIndex = Math.max(index - 1, 0);
        setKeyboardSelectedIndex(prevIndex);
        setTimeout(() => optionsRefs.current[prevIndex]?.focus(), 0);
        break;
      }
      case 'Escape':
        e.preventDefault();
        setIsBusinessTypeOpen(false);
        setKeyboardSelectedIndex(-1);
        break;
      default: {
        // Type-ahead: jump to option starting with typed character
        if (e.key.length === 1 && /^[a-z0-9]$/i.test(e.key)) {
          e.preventDefault();
          if (typeAheadTimeoutRef.current) {
            clearTimeout(typeAheadTimeoutRef.current);
          }
          typeAheadRef.current += e.key.toLowerCase();
          const matchedIndex = kycBusinessTypes.findIndex((item) =>
            item.value.toLowerCase().startsWith(typeAheadRef.current)
          );
          if (matchedIndex !== -1) {
            setKeyboardSelectedIndex(matchedIndex);
            setTimeout(() => optionsRefs.current[matchedIndex]?.focus(), 0);
          }
          typeAheadTimeoutRef.current = setTimeout(() => {
            typeAheadRef.current = '';
          }, 500);
        }
        break;
      }
    }
  };

  if (loading) {
    return <AuthBrandedLoader message='Loading…' />;
  }

  return (
    <section>
      <motion.main className={`${styles.main} auth-onboarding-card`} initial={false}>
        <div className={styles.formOuter}>
          <form
            className={`${styles.form} ph-no-capture`}
            noValidate
            onSubmit={formik.handleSubmit}
            data-clarity-mask='true'
          >
            <div className={styles.formInner}>
              <div className={styles.formBoxes}>
                {/* Company Legal Name */}
                <div className={styles.nameBox}>
                  <div className='inputWrapper' title='Company legal name'>
                    <label className='label'>
                      Company legal name<span className={styles.mandatory}>*</span>
                    </label>
                    <div
                      className={clsx(
                        formik.touched.name && formik.errors.name ? styles.isInvalid : '',
                        formik.touched.name && !formik.errors.name ? styles.valid : '',
                        `inputField`
                      )}
                    >
                      <input
                        type='text'
                        autoComplete='off'
                        name='name'
                        value={formik.values.name}
                        onChange={(e) => {
                          formik.handleChange(e);
                          formik.setFieldTouched('name', true);
                        }}
                        onBlur={async (e) => {
                          formik.handleBlur(e);
                          // Wait for validation to complete, then trim if valid
                          const error = await formik.validateField('name');
                          if (!error) {
                            const trimmedValue = e.target.value.trimStart();
                            if (trimmedValue !== e.target.value) {
                              formik.setFieldValue('name', trimmedValue);
                            }
                          }
                        }}
                        placeholder='Enter Company Legal Name'
                        onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                      />
                    </div>
                    {formik.touched.name && formik.errors.name && (
                      <div className='text-red-500 text-sm mt-1'>{formik.errors.name}</div>
                    )}
                  </div>

                  {/* Business Name */}
                  <div className='inputWrapper' title='Business name'>
                    <label className='label'>Business name</label>
                    <div
                      className={clsx(
                        formik.touched.displayName && formik.errors.displayName
                          ? styles.isInvalid
                          : '',
                        formik.touched.displayName && !formik.errors.displayName
                          ? styles.valid
                          : '',
                        `inputField`
                      )}
                    >
                      <input
                        type='text'
                        autoComplete='off'
                        name='displayName'
                        value={formik.values.displayName}
                        onChange={(e) => {
                          formik.handleChange(e);
                          formik.setFieldTouched('displayName', true);
                        }}
                        onBlur={async (e) => {
                          formik.handleBlur(e);
                          // Wait for validation to complete, then trim if valid
                          const error = await formik.validateField('displayName');
                          if (!error) {
                            const trimmedValue = e.target.value.trimStart();
                            if (trimmedValue !== e.target.value) {
                              formik.setFieldValue('displayName', trimmedValue);
                            }
                          }
                        }}
                        placeholder='Enter Business Name'
                        onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                      />
                    </div>
                    {formik.touched.displayName && formik.errors.displayName && (
                      <div className='text-red-500 text-sm mt-1'>{formik.errors.displayName}</div>
                    )}
                  </div>

                  {/* Business Type Dropdown */}
                  <div className='inputWrapper' title='Business type'>
                    <label className='label'>
                      Business type<span className={styles.mandatory}>*</span>
                    </label>
                    <ClickAwayListener onClickAway={() => setIsBusinessTypeOpen(false)}>
                      <div className={loginStyles.customSelectRoot}>
                        <button
                          type='button'
                          className={clsx(
                            loginStyles.customSelectTrigger,
                            formik.touched.kycBusinessTypeId && formik.errors.kycBusinessTypeId
                              ? styles.isInvalid
                              : '',
                            formik.touched.kycBusinessTypeId && !formik.errors.kycBusinessTypeId
                              ? styles.valid
                              : '',
                            isBusinessTypeOpen && loginStyles.customSelectTriggerOpen,
                            'inputField'
                          )}
                          onClick={() => setIsBusinessTypeOpen((prev) => !prev)}
                          onKeyDown={handleBusinessTypeKeyDown}
                          aria-haspopup='listbox'
                          aria-expanded={isBusinessTypeOpen}
                        >
                          <span
                            className={clsx(
                              loginStyles.customSelectValue,
                              !formik.values.kycBusinessTypeId && loginStyles.customSelectPlaceholder
                            )}
                          >
                            {selectedTypeLabel || 'Select business type'}
                          </span>
                          <span
                            className={clsx(
                              loginStyles.customSelectChevron,
                              isBusinessTypeOpen && loginStyles.customSelectChevronOpen
                            )}
                            aria-hidden
                          />
                        </button>
                        {isBusinessTypeOpen && (
                          <div className={loginStyles.customSelectMenu} role='listbox'>
                            {kycBusinessTypes?.map((type, index) => {
                              const isSelected =
                                formik.values.kycBusinessTypeId?.toString() ===
                                type.kycBusinessTypeId.toString();
                              return (
                                <button
                                  key={type.kycBusinessTypeId}
                                  ref={(el) => {
                                    optionsRefs.current[index] = el;
                                  }}
                                  type='button'
                                  role='option'
                                  aria-selected={isSelected}
                                  className={clsx(
                                    loginStyles.customSelectOption,
                                    isSelected && loginStyles.customSelectOptionSelected
                                  )}
                                  onClick={() => {
                                    formik.setFieldValue(
                                      'kycBusinessTypeId',
                                      type.kycBusinessTypeId.toString()
                                    );
                                    formik.setFieldTouched('kycBusinessTypeId', true);
                                    setIsBusinessTypeOpen(false);
                                  }}
                                  onKeyDown={(e) => handleOptionKeyDown(index, e, type)}
                                >
                                  {type.value}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </ClickAwayListener>
                    {formik.touched.kycBusinessTypeId && formik.errors.kycBusinessTypeId && (
                      <div className='text-red-500 text-sm mt-1'>
                        {formik.errors.kycBusinessTypeId}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className='flex gap-4'>
              <button onClick={handleBackClick} type='button' className='cancelBtn'>
                Back
              </button>
              <button
                type='submit'
                disabled={!formik.isValid}
                className={`submitBtn ${
                  (formik.isSubmitting || !formik.isValid) && styles.disableBtn
                }`}
              >
                Next
              </button>
            </div>
          </form>
        </div>
      </motion.main>
    </section>
  );
};

export default CompanyInformation;
